/**
 * Grouped notification feed (Today action/updates + Earlier summary).
 */

import React from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import NotificationRow from "./NotificationRow.jsx";
import {
  groupNotificationsForFeed,
  summarizeEarlierByCategory,
} from "../utils/grouping.js";
import {
  CATEGORY_ACCENT,
  INBOX_TABS,
  resolveNotificationCategory,
} from "../utils/taxonomy.js";
import { getCategoryIconComponent } from "../utils/icons.js";

const SectionHeader = ({ title, count, color, expanded, onToggle }) => (
  <Box
    onClick={onToggle}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      px: 2,
      py: 1.25,
      bgcolor: `${color}12`,
      borderBottom: "1px solid",
      borderColor: "divider",
      cursor: "pointer",
      userSelect: "none",
    }}
  >
    <Typography variant="subtitle2" fontWeight={700} sx={{ color }}>
      {title} ({count})
    </Typography>
    <ExpandMore
      sx={{
        color,
        transform: expanded ? "rotate(180deg)" : "none",
        transition: "transform 0.2s ease",
      }}
    />
  </Box>
);

const NotificationFeed = ({
  notifications,
  loading,
  onOpen,
  onMarkRead,
  onDelete,
  onSelectCategory,
  emptyMessage = "No notifications yet",
  isMobile = false,
}) => {
  const [expanded, setExpanded] = React.useState({
    action: true,
    updates: true,
    earlier: true,
  });

  if (loading) {
    return (
      <Box sx={{ py: 8, displayAlign: "center" }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading notifications...
        </Typography>
      </Box>
    );
  }

  if (!notifications?.length) {
    return (
      <Box sx={{ py: 8, textAlign: "center" }}>
        <Typography color="text.secondary">{emptyMessage}</Typography>
      </Box>
    );
  }

  const { todayAction, todayUpdates, earlier } =
    groupNotificationsForFeed(notifications);
  const earlierSummary = summarizeEarlierByCategory(
    earlier,
    resolveNotificationCategory
  );
  const hasToday = todayAction.length > 0 || todayUpdates.length > 0;

  return (
    <Box>
      {hasToday && (
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            fontWeight={700}
            letterSpacing={1.2}
          >
            Today
          </Typography>
        </Box>
      )}

      {todayAction.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <SectionHeader
            title="Action Required"
            count={todayAction.length}
            color={CATEGORY_ACCENT.action_required}
            expanded={expanded.action}
            onToggle={() =>
              setExpanded((s) => ({ ...s, action: !s.action }))
            }
          />
          {expanded.action &&
            todayAction.map((n) => (
              <NotificationRow
                key={n.id}
                notification={n}
                onOpen={onOpen}
                onMarkRead={onMarkRead}
                onDelete={onDelete}
                isMobile={isMobile}
              />
            ))}
        </Box>
      )}

      {todayUpdates.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <SectionHeader
            title="Updates"
            count={todayUpdates.length}
            color={CATEGORY_ACCENT.updates}
            expanded={expanded.updates}
            onToggle={() =>
              setExpanded((s) => ({ ...s, updates: !s.updates }))
            }
          />
          {expanded.updates &&
            todayUpdates.map((n) => (
              <NotificationRow
                key={n.id}
                notification={n}
                onOpen={onOpen}
                onMarkRead={onMarkRead}
                onDelete={onDelete}
                isMobile={isMobile}
              />
            ))}
        </Box>
      )}

      {earlier.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              fontWeight={700}
              letterSpacing={1.2}
            >
              Earlier
            </Typography>
          </Box>
          <SectionHeader
            title="Earlier notifications"
            count={earlier.length}
            color={CATEGORY_ACCENT.all}
            expanded={expanded.earlier}
            onToggle={() =>
              setExpanded((s) => ({ ...s, earlier: !s.earlier }))
            }
          />
          {expanded.earlier && (
            <>
              {earlierSummary.length > 0 && (
                <Stack
                  direction="row"
                  spacing={1.25}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ p: isMobile ? 1.25 : 2 }}
                >
                  {earlierSummary.map(({ category, count }) => {
                    const Icon = getCategoryIconComponent(category);
                    const label =
                      INBOX_TABS.find((t) => t.id === category)?.label ||
                      category;
                    const accent =
                      CATEGORY_ACCENT[category] || CATEGORY_ACCENT.all;
                    return (
                      <Card
                        key={category}
                        elevation={0}
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          minWidth: isMobile ? "auto" : 140,
                        }}
                      >
                        <CardActionArea
                          onClick={() => onSelectCategory?.(category)}
                        >
                          <CardContent
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.25,
                              py: 1.5,
                              "&:last-child": { pb: 1.5 },
                            }}
                          >
                            <Box
                              sx={{
                                color: accent,
                                display: "flex",
                              }}
                            >
                              <Icon fontSize="small" />
                            </Box>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {count}
                              </Typography>
                            </Box>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    );
                  })}
                </Stack>
              )}
              {earlier.map((n) => (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  onOpen={onOpen}
                  onMarkRead={onMarkRead}
                  onDelete={onDelete}
                  isMobile={isMobile}
                />
              ))}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default NotificationFeed;
